import { useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import Tag from "../../../fragments/Tag"
import { notificationActions } from "../../../../store/notifications"
import { caseQuickUpdate } from "../../../../lib/fetchApi"
import { caseActions } from "../../../../store/case"
import LiabilitySidebar from "./LiabilitySidebar"
import CardGrey from "../../../common/CardGrey"
import { frontendOptions, liabilityPolicyLimitsOptions, liabilityPolicyStatusOptionsPayerTypes, shouldShowFieldUpdateLoader } from "../../../../lib/systemData"
import DriverInfo from "./DriverInfo"
import VehicleOwnerInfo from "./VehicleOwnerInfo"
import DeleteItem from "../Shared/DeleteItem"
import Item from "../Shared/Item"
import PrimaryCheckbox from "../Shared/PrimaryCheckbox"
import InsuranceAddress from "../Shared/InsuranceAddress"
import { formatDate, getLabelFromSystemSettings } from "../../../../lib/script"
import CustomSelect from "../../../common/interactive/CustomSelect"
import GroupContainer from "../GroupNew/GroupContainer"
import DefaultUpdater from "../../../common/DefaultUpdater"
import CopyIconButton from "../../../fragments/CopyIconButton"
import { copyLineAlways, getInsuranceLocationContact, getLastNoteForType, hasCopyValue, joinCopyLines } from "../../../../lib/insuranceCopyNotes"

import classes from '../Shared/Item.module.css'
import PayerTypeStatusGlobalModal from "../../../common/modal/PayerTypeStatusGlobalModal"
import VerificationsHistory from "../verifications/VerificationsHistory"
import { useUserTabEditPermissionDisabled } from "../../../../lib/usersPermissions"

const LiabilityItem = ({data, lastChild, apiCase}) => {
    const dispatch = useDispatch() 

    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase) 
    const user = useSelector(state => state.user.user)
    const canToggleLimitsConfirmed = !useUserTabEditPermissionDisabled('liability_confirmed_with_carrier')
    const limitsEditAfterConfirmedDisabled = useUserTabEditPermissionDisabled('liability_edit_confirmed_limits')
    const limitsConfirmed = Boolean(data.limits_confirmed_with_carrier)
    const limitsLockedForUser = limitsConfirmed && limitsEditAfterConfirmedDisabled
    const limitsLockTitle = limitsLockedForUser
        ? 'Limits are confirmed with the carrier. Contact Verification to change the value.'
        : undefined

    const [isLiabilityStatusSaving, setIsLiabilityStatusSaving] = useState(false)
    const liabilityStatusSeqRef = useRef(0)

    const selectAssumedHandler = async (value) => {    
        let today = formatDate(new Date())

        const body = {
            type: apiCase,
            type_id: data.id,
            fields: {
                liability_status: value.data.value,
            }    
        }        
        
        dispatch(caseActions.modifyActiveCaseMultiple({
            folder: 'liabilities',
            item: {
                itemId: data.id,
                fields: [
                    {
                        field: 'liability_status',
                        value: value.data.value
                    },
                    {
                        field: 'verified_date',
                        value: today
                    },
                    {
                        field: 'verified_by',
                        value: user.name
                    },
                    {
                        field: 'verified',
                        value: true
                    }
                ]            
            }
        }))

        body.fields.verified = true
        body.fields.verified_by = user.name
        body.fields.verified_date = today
    
        

        const caseId = activeCase?.case?.id
        if (!caseId) {
            return
        }

        const requestSeq = ++liabilityStatusSeqRef.current
        setIsLiabilityStatusSaving(true)
        try {
            const update = await caseQuickUpdate(userToken, caseId, body, dispatch, null, {
                staleGuardKey: `${caseId}:liability:${data.id}:liability_status`,
                staleGuardSequence: requestSeq,
            })
            if (update?.staleResponse) {
                return
            }
            if (update && update.errorMessage) {
                dispatch(notificationActions.setError(update))
            }
        } finally {
            if (liabilityStatusSeqRef.current === requestSeq) {
                setIsLiabilityStatusSaving(false)
            }
        }
    }

    let merge_before_update_at_fault = {
        put_in: 'array',
        items: [
            {
                value_flag: 'in_all_cases',
                fields_to_modify: [
                    {
                        field: 'ins_owner',
                        value: null
                    },
                ]
            }
        ],
    
    }

    const updateLinkedPolicyLimits = async (patientAmount, accidentAmount) => {
        if (limitsLockedForUser) {
            return
        }

        dispatch(caseActions.modifyActiveCaseMultiple({
            folder: 'liabilities',
            item: {
                itemId: data.id,
                fields: [
                    {
                        field: 'policy_limit_patient',
                        value: patientAmount
                    },
                    {
                        field: 'policy_limit_accident',
                        value: accidentAmount
                    }
                ]
            }
        }))

        const update = await caseQuickUpdate(userToken, activeCase.case.id, {
            type: apiCase,
            type_id: data.id,
            fields: {
                policy_limit_patient: patientAmount,
                policy_limit_accident: accidentAmount
            }
        }, dispatch)

        if (update && update.errorMessage) {
            dispatch(notificationActions.setError(update))
        }
    }

    const liabilityLimitPatientSuggestions = (liabilityPolicyLimitsOptions() || [])
        .filter((option) => option.active == 1 && option.amount_patient != null && option.amount_patient !== '')
        .map((option) => ({
            value: option.amount_patient,
            label: option.name,
            description: `${option.amount_patient}`,
            searchLabel: `${option.name} ${option.amount_patient} ${option.amount_insurance ?? ''}`,
            linkedValue: option.amount_insurance
        }))

    const liabilityLimitAccidentSuggestions = (liabilityPolicyLimitsOptions() || [])
        .filter((option) => option.active == 1 && option.amount_insurance != null && option.amount_insurance !== '')
        .map((option) => ({
            value: option.amount_insurance,
            label: option.name,
            description: `${option.amount_insurance}`,
            searchLabel: `${option.name} ${option.amount_insurance} ${option.amount_patient ?? ''}`,
            linkedValue: option.amount_patient
        }))

    const liabilityLoc = getInsuranceLocationContact(data, "liabilities", activeCase)
    const liabilityLastNote = getLastNoteForType(activeCase?.notes, 3)

      const liabilityLimitParts = []
    if (hasCopyValue(data.policy_limit_patient)) {
        liabilityLimitParts.push(String(data.policy_limit_patient).trim())
    }
    if (hasCopyValue(data.policy_limit_accident)) {
        liabilityLimitParts.push(String(data.policy_limit_accident).trim())
    }
    const liabilityPolicyLimits = liabilityLimitParts.length ? liabilityLimitParts.join(" / ") : null

    const liabilityAssumedLabel = getLabelFromSystemSettings(frontendOptions()?.assumedOptions, data.liability_status)
    const liabilityAdjusterName = [data.adjuster?.first_name, data.adjuster?.last_name].filter(Boolean).join(" ").trim() || null

    const liabilityCopyBody = joinCopyLines([
        copyLineAlways("Liab Co", data.insurance_carrier?.name),
        copyLineAlways("Claim No", data.claim_number),
        copyLineAlways("Policy Limits", liabilityPolicyLimits),
        copyLineAlways("Adjuster", liabilityAdjusterName),
        copyLineAlways("Adjuster Phone", data.adjuster?.contact_information?.phone),
        copyLineAlways("Adjuster Fax", data.adjuster?.contact_information?.fax),
        hasCopyValue(liabilityAssumedLabel) ? `Assumed? ${liabilityAssumedLabel}` : "Assumed?",
        copyLineAlways("Address", liabilityLoc.address1),
        copyLineAlways("Adjuster Email", data.adjuster?.contact_information?.email),
        copyLineAlways("City", liabilityLoc.city),
        copyLineAlways("State", liabilityLoc.state),
        copyLineAlways("Zip", liabilityLoc.zip),
    ])
    const liabilityCopyText = `${liabilityCopyBody}\n\n${copyLineAlways("Note", liabilityLastNote)}`

    return <Item data={data} mainItem={data.is_primary_insurance} type={apiCase} folder={'liabilities'} lastChild={lastChild}>
        <div className={classes.item_header}>
            {data.insurance_carrier && <>
                <div>
                    <div className={classes.name}>
                        <CopyIconButton textToCopy={liabilityCopyText} title="Copy liability notes" />
                        <Tag type={'note'}>{data.insurance_carrier.code}</Tag>
                        <h2>{data.insurance_carrier.name}</h2>                        
                    </div>
                    {data.insurance_carrier.contact_information && <div className={classes.location}>
                        <i className='ti ti-building-skyscraper'></i>
                        <InsuranceAddress data={data} folder={'liabilities'}/>
                    </div>}
                </div>
                <div className={classes.header_controls}>
                    {/* <IsVerifiedTag isVerified={data.verified}>{data.verified ? 'Verified' : 'Not Verified'}</IsVerifiedTag> */}
                    <div className={classes.sidebar_row_block}>
                    {/* !data.claim_number Claim # is required*/}
                        <CustomSelect
                            label={'Liability'}
                            optionsData={frontendOptions()?.assumedOptions}
                            action={selectAssumedHandler}
                            preSelectedValue={data.liability_status}
                            hasRemove={true}
                            isDisabled={!data.verified}
                            noticeDisabled={['Insurance is not verified']}
                            identifier={{type: apiCase}}
                            apiDisabled={isLiabilityStatusSaving}
                            showApiLoader={shouldShowFieldUpdateLoader()}
                        />
                    </div> 
                    <DeleteItem data={data} type={apiCase} folder={'liabilities'} label={'liability'}/>
                </div>
            </>}
        </div>
        <div className={classes.item_inner}>
            <div className={classes.item_left}>
                
                <div className={classes.item_body}>
                    <div className={classes.row}>
                        <CardGrey>
                            <PrimaryCheckbox data={data} label={'Primary Insurance'} folder={'liabilities'} apiCase={apiCase} propertyName={'is_primary_insurance'}/>                           
                        </CardGrey>            
                        
                            <div className="flex-container">
                                <CardGrey>
                                    <DefaultUpdater type={'radio'} value={data.at_fault} options={frontendOptions()?.AT_Fault_Radio} name={'at_fault'} identifier={{type: apiCase, field: 'at_fault', type_id: data.id, folder: 'liabilities'}} extraOptions={{normalSize: true}} merge_before_update={merge_before_update_at_fault}/>
                                </CardGrey>
                                {(Number(data.at_fault) == 2) ? <CardGrey>
                                    <div style={{flex: 2}}>                                            
                                        <DefaultUpdater type={'radio'} value={data.ins_owner} options={frontendOptions()?.liabilityOwnerRadio} name={'ins_owner'} identifier={{type: apiCase, field: 'ins_owner', type_id: data.id, folder: 'liabilities'}} extraOptions={{normalSize: true}}/>                                           
                                    </div>
                                </CardGrey> : ''}
                                {canToggleLimitsConfirmed ? <CardGrey>
                                    <DefaultUpdater
                                        type={'checkbox'}
                                        value={data.limits_confirmed_with_carrier}
                                        name={'limits_confirmed_with_carrier'}
                                        label={'Limits Confirmed w/ Carrier'}
                                        identifier={{
                                            type: apiCase,
                                            field: 'limits_confirmed_with_carrier',
                                            type_id: data.id,
                                            folder: 'liabilities',
                                            permissionType: 'liability_confirmed_with_carrier',
                                        }}
                                        extraOptions={{direction: 'reverse'}}
                                    />
                                </CardGrey> : null}
                            </div>
                    </div>
                    
                    <div className={classes.row}>
                        <div className={classes.row_item_full}>
                            <CardGrey>
                                <div className="flex-container-equal">
                                    <DefaultUpdater type={'input'} value={data.insured_name} label={'Name of Insured'} identifier={{type: apiCase, field: 'insured_name', type_id: data.id, folder: 'liabilities'}} />
            
                                    <DefaultUpdater type={'input'} value={data.policy_number} label={'Policy Number'} identifier={{type: apiCase, field: 'policy_number', type_id: data.id, folder: 'liabilities'}} />
                                    {/* extraOptions={{redBorderFlag: !data.policy_number}} */}
      
                                    <DefaultUpdater
                                        type={'input'}
                                        value={data.policy_limit_patient}
                                        label={'Liability Limit Patient'}
                                        identifier={{type: apiCase, field: 'policy_limit_patient', type_id: data.id, folder: 'liabilities'}}
                                        extraOptions={{
                                            format: 'currency',
                                            redBorderFlag: !limitsConfirmed && !data.policy_limit_patient,
                                            greenBorderFlag: limitsConfirmed,
                                            isNotModifiable: limitsLockedForUser,
                                            title: limitsLockTitle,
                                            suggestions: limitsLockedForUser ? undefined : liabilityLimitPatientSuggestions,
                                            onSuggestionSelect: limitsLockedForUser
                                                ? undefined
                                                : (option) => updateLinkedPolicyLimits(`${option.value}`, option.linkedValue != null ? `${option.linkedValue}` : ''),
                                        }}
                                    />
                                    <DefaultUpdater
                                        type={'input'}
                                        value={data.policy_limit_accident}
                                        label={'Liability Limit Accident'}
                                        identifier={{type: apiCase, field: 'policy_limit_accident', type_id: data.id, folder: 'liabilities'}}
                                        extraOptions={{
                                            format: 'currency',
                                            greenBorderFlag: limitsConfirmed,
                                            isNotModifiable: limitsLockedForUser,
                                            title: limitsLockTitle,
                                            suggestions: limitsLockedForUser ? undefined : liabilityLimitAccidentSuggestions,
                                            onSuggestionSelect: limitsLockedForUser
                                                ? undefined
                                                : (option) => updateLinkedPolicyLimits(option.linkedValue != null ? `${option.linkedValue}` : '', `${option.value}`),
                                        }}
                                    />
                  
                                    <PayerTypeStatusGlobalModal date={data.policy_status_date} user={data.policy_status_by} isDisabled={!data.policy_status}>    
                                        <DefaultUpdater type={'select'} value={Number(data.policy_status)} options={liabilityPolicyStatusOptionsPayerTypes()} label={'Claim Status'} identifier={{type: apiCase, field: 'policy_status', type_id: data.id, folder: 'liabilities'}} extraOptions={{hasRemove: true, borderRed: (!data.policy_status && data.liability_status == 1)}}/>
                                    </PayerTypeStatusGlobalModal>  
                                                        
                                    <DefaultUpdater type={'date'} value={data.effective_date} label={'Effective Date'} identifier={{type: apiCase, field: 'effective_date', type_id: data.id, folder: 'liabilities'}}/>
                                  
                                </div>
                            </CardGrey>
                        </div>                        
                    </div>

                    <div className={classes.row}>

                        <div className={classes.row_item_full}>
                        <CardGrey>
                            <VerificationsHistory data={data} itemId={data.id} verificationHistory={data.verification_history} folder={'liabilities'} verificationField={'verification_history'} caseType={apiCase}/>
                        </CardGrey>
                        </div>
                    </div>

                    <div className={classes.row}>
                        <div className={classes.row_item_full}>
                            <CardGrey>
                            
                                {/* <GeneralVerifiedBy data={data} folder={'liabilities'} caseType={apiCase} areFieldsNotModifiable={disableVerifySelection(data, 'liability')}/>  */}
                                
                                {/* Location  */}
                                <div className={classes.group_container}>
                                    <GroupContainer data={data} smsMail={'Insurance Carrier'} dataId={data.id} label={'location'} folder={'liabilities'} apiCase={apiCase} fieldsData={{fields: ['location_id', 'location'], fieldId: 'location_id', fieldobj: 'location', group: 'locations', inputLabel: 'Location'}}/>
                                </div>

                                {/* Adjuster */}
                                <div className={classes.group_container}>
                                    <GroupContainer data={data} smsMail={'Adjuster'} dataId={data.id} label={'adjuster'} folder={'liabilities'} apiCase={apiCase} fieldsData={{fields: ['adjuster_id', 'adjuster'], fieldId: 'adjuster_id', fieldobj: 'adjuster', group: 'adjusters', inputLabel: 'Adjuster'}}/>
                                </div>
                            </CardGrey>
                        </div>
                    </div>

                    <div className={classes.row}>
                        <CardGrey>
                            <DriverInfo data={data} apiCase={apiCase}/>
                            <div className={classes.separator}></div>
                            <VehicleOwnerInfo data={data} apiCase={apiCase}/>
                        </CardGrey>
                    </div>
                    
                </div>
                
            </div>

            <div className={classes.item_right}>
                <LiabilitySidebar data={data} apiCase={apiCase}/>
            </div>      
        </div> 
    </Item>
}

export default LiabilityItem